/// Types
import { useEffect, useMemo, useState } from 'react';
import type React from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
/// Slices
import { selectObjectTypesList } from 'slices/objectTypes.slice';
import { findAllCombinations } from 'utils/combination';
import type { TReservationTemplate, TType } from '@timeedit/types/lib/types';
import { Card, Empty, Table } from '@timeedit/ui-components/antd';
import type { ColumnType } from '@timeedit/ui-components/antd/table/index';
/// Styles
import './TypeCombination.scss';

interface Combination {
  key: string;
  data: number[];
}
interface TypeCombinationProps {
  template: TReservationTemplate;
  onChange: (combinations: number[][]) => void;
}

const TypeCombination = (props: TypeCombinationProps) => {
  const { template, onChange: onChangeCombinations } = props;
  const [typeCombinations, setTypeCombinations] = useState<React.Key[]>([]);
  useEffect(() => {
    setTypeCombinations(
      (template.typeCombinations || []).map((combination: number[]) => {
        const ids = _.sortBy(combination);
        return ids.join('-');
      })
    );
  }, [template]);
  const combinationTypeIds: number[] = useMemo(() => {
    const combinationTypes = (template.types || []).filter(
      (type: any) => type.mandatoryProperty === 'COMBINATION'
    );
    const ids: number[] = combinationTypes.map((type: any) => type.type);
    return _.sortBy(ids);
  }, [template.types]);
  const allCombinations = useMemo(() => {
    return findAllCombinations([...combinationTypeIds]);
  }, [combinationTypeIds]);
  const allTypes: TType[] = useSelector(selectObjectTypesList);
  const typeById = _.keyBy(allTypes, 'id');
  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[]) => {
      setTypeCombinations(selectedRowKeys);
      onChangeCombinations(
        selectedRowKeys.map((key) =>
          (key as string).split('-').map((val) => Number(val))
        )
      );
    },
    selectedRowKeys: typeCombinations,
  };
  const data: Combination[] = allCombinations.map((combination) => ({
    key: combination.filter((id) => id !== -1).join('-'),
    data: combination,
  }));
  const columns: ColumnType<Combination>[] = combinationTypeIds.map(
    (id, idx) => ({
      title: id,
      render: ({ data }) => {
        const typeId = data[idx];
        if (typeId === -1) return <span style={{ margin: 5 }}>-</span>;
        return (
          <span style={{ margin: 5, textWrap: 'pretty' }}>
            {_.get(typeById, [typeId, 'name'])}
          </span>
        );
      },
      align: 'center',
      width: 140,
    })
  );
  return (
    <div className="type-combination">
      <Card type="inner" title="Combinations">
        {combinationTypeIds.length ? (
          <Table
            rowSelection={rowSelection}
            dataSource={data}
            columns={columns}
            pagination={false}
            showHeader={false}
            size="small"
            data-testid="combinations"
            virtual
            scroll={{ y: 300, x: 700 }}
          />
        ) : (
          <Empty description="No combinations" />
        )}
      </Card>
    </div>
  );
};

export default TypeCombination;
